// /app/frontend/src/pages/admin/SuperAdminDashboard.jsx
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Building2, Users, FileText, Shield, AlertTriangle, TrendingUp,
  Plus, Search, Filter, MoreVertical, Eye, Edit, Trash2, Power,
  Check, X, DollarSign, Activity, Globe, Settings, RefreshCw
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
} from '../../components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../../components/ui/dropdown-menu';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import { Label } from '../../components/ui/label';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const SuperAdminDashboard = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [platformStats, setPlatformStats] = useState(null);
  const [organizations, setOrganizations] = useState([]);
  const [selectedOrg, setSelectedOrg] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [tierFilter, setTierFilter] = useState('all');
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showDetailDialog, setShowDetailDialog] = useState(false);
  const [syncing, setSyncing] = useState(false);

  // New organization form state
  const [newOrgForm, setNewOrgForm] = useState({
    name: '',
    slug: '',
    contact_email: '',
    subscription_tier: 'starter',
    owner_name: '',
    owner_email: '',
    owner_password: ''
  });

  const getAuthHeaders = () => {
    const token = localStorage.getItem('token');
    return {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json'
    };
  };

  const fetchPlatformStats = async () => {
    try {
      const response = await fetch(`${API_URL}/api/super-admin/platform-stats`, {
        headers: getAuthHeaders()
      });
      if (response.ok) {
        const data = await response.json();
        setPlatformStats(data);
      }
    } catch (error) {
      console.error('Failed to fetch platform stats:', error);
    }
  };

  const fetchOrganizations = async () => {
    try {
      let url = `${API_URL}/api/super-admin/organizations`;
      const params = new URLSearchParams();
      if (statusFilter !== 'all') params.append('status', statusFilter);
      if (tierFilter !== 'all') params.append('tier', tierFilter);
      if (searchTerm) params.append('search', searchTerm);
      if (params.toString()) url += `?${params.toString()}`;

      const response = await fetch(url, { headers: getAuthHeaders() });
      if (response.ok) {
        const data = await response.json();
        setOrganizations(data);
      }
    } catch (error) {
      console.error('Failed to fetch organizations:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchOrgDetail = async (orgId) => {
    try {
      const response = await fetch(`${API_URL}/api/super-admin/organizations/${orgId}`, {
        headers: getAuthHeaders()
      });
      if (response.ok) {
        const data = await response.json();
        setSelectedOrg(data);
        setShowDetailDialog(true);
      }
    } catch (error) {
      console.error('Failed to fetch org detail:', error);
    }
  };

  const createOrganization = async () => {
    try {
      const response = await fetch(`${API_URL}/api/super-admin/organizations`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify(newOrgForm)
      });
      if (response.ok) {
        toast.success('Organization created successfully');
        setShowCreateDialog(false);
        setNewOrgForm({
          name: '', slug: '', contact_email: '', subscription_tier: 'starter',
          owner_name: '', owner_email: '', owner_password: ''
        });
        fetchOrganizations();
        fetchPlatformStats();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to create organization');
      }
    } catch (error) {
      toast.error('Failed to create organization');
    }
  };

  const updateOrgStatus = async (orgId, action) => {
    try {
      const response = await fetch(`${API_URL}/api/super-admin/organizations/${orgId}/${action}`, {
        method: 'POST',
        headers: getAuthHeaders()
      });
      if (response.ok) {
        toast.success(`Organization ${action}d successfully`);
        fetchOrganizations();
        if (selectedOrg && selectedOrg.id === orgId) {
          fetchOrgDetail(orgId);
        }
      }
    } catch (error) {
      toast.error(`Failed to ${action} organization`);
    }
  };

  const syncOrgStats = async () => {
    setSyncing(true);
    try {
      const response = await fetch(`${API_URL}/api/super-admin/sync-org-stats`, {
        method: 'POST',
        headers: getAuthHeaders()
      });
      if (response.ok) {
        toast.success('Organization stats synced successfully');
        fetchOrganizations();
        fetchPlatformStats();
      }
    } catch (error) {
      toast.error('Failed to sync stats');
    } finally {
      setSyncing(false);
    }
  };

  useEffect(() => {
    fetchPlatformStats();
    fetchOrganizations();
  }, [statusFilter, tierFilter]);

  useEffect(() => {
    const debounce = setTimeout(() => {
      fetchOrganizations();
    }, 300);
    return () => clearTimeout(debounce);
  }, [searchTerm]);

  const getStatusBadge = (status) => {
    const variants = {
      active: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-400',
      suspended: 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-400',
      canceled: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400',
      onboarding: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400'
    };
    return <Badge className={variants[status] || variants.active}>{status}</Badge>;
  };

  const getTierBadge = (tier) => {
    const variants = {
      free: 'bg-slate-100 text-slate-800 dark:bg-slate-800 dark:text-slate-300',
      starter: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400',
      professional: 'bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-400',
      enterprise: 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-400'
    };
    return <Badge className={variants[tier] || variants.starter}>{tier}</Badge>;
  };

  if (loading) {
    return (
      <div className="p-6 space-y-6">
        <div className="animate-pulse space-y-4">
          <div className="h-8 bg-slate-200 dark:bg-slate-700 rounded w-1/4" />
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {[1, 2, 3, 4].map(i => (
              <div key={i} className="h-32 bg-slate-200 dark:bg-slate-700 rounded" />
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6" data-testid="super-admin-dashboard">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-slate-900 dark:text-white">
            Platform Administration
          </h1>
          <p className="text-slate-600 dark:text-slate-400 mt-1">
            Manage tenant organizations and monitor platform health
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={syncOrgStats} disabled={syncing}>
            <RefreshCw className={`h-4 w-4 mr-2 ${syncing ? 'animate-spin' : ''}`} />
            Sync Stats
          </Button>
          <Button onClick={() => setShowCreateDialog(true)} data-testid="create-org-btn">
            <Plus className="h-4 w-4 mr-2" />
            New Organization
          </Button>
        </div>
      </div>

      {/* Platform Stats Cards */}
      {platformStats && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 xl:grid-cols-6 gap-4">
          <Card className="border-l-4 border-l-teal-500">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-600 dark:text-slate-400">Organizations</p>
                  <p className="text-2xl font-bold text-slate-900 dark:text-white">
                    {platformStats.total_organizations}
                  </p>
                  <p className="text-xs text-emerald-600">{platformStats.active_organizations} active</p>
                </div>
                <Building2 className="h-10 w-10 text-teal-500 opacity-50" />
              </div>
            </CardContent>
          </Card>

          <Card className="border-l-4 border-l-blue-500">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-600 dark:text-slate-400">Total Users</p>
                  <p className="text-2xl font-bold text-slate-900 dark:text-white">
                    {platformStats.total_users}
                  </p>
                </div>
                <Users className="h-10 w-10 text-blue-500 opacity-50" />
              </div>
            </CardContent>
          </Card>

          <Card className="border-l-4 border-l-purple-500">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-600 dark:text-slate-400">Total Clients</p>
                  <p className="text-2xl font-bold text-slate-900 dark:text-white">
                    {platformStats.total_clients}
                  </p>
                </div>
                <FileText className="h-10 w-10 text-purple-500 opacity-50" />
              </div>
            </CardContent>
          </Card>

          <Card className="border-l-4 border-l-amber-500">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-600 dark:text-slate-400">Total Entities</p>
                  <p className="text-2xl font-bold text-slate-900 dark:text-white">
                    {platformStats.total_entities}
                  </p>
                </div>
                <Globe className="h-10 w-10 text-amber-500 opacity-50" />
              </div>
            </CardContent>
          </Card>

          <Card className="border-l-4 border-l-emerald-500">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-600 dark:text-slate-400">Est. MRR</p>
                  <p className="text-2xl font-bold text-slate-900 dark:text-white">
                    ${platformStats.mrr_estimate?.toLocaleString()}
                  </p>
                </div>
                <DollarSign className="h-10 w-10 text-emerald-500 opacity-50" />
              </div>
            </CardContent>
          </Card>

          <Card className="border-l-4 border-l-rose-500">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-600 dark:text-slate-400">New (30d)</p>
                  <p className="text-2xl font-bold text-slate-900 dark:text-white">
                    {platformStats.recent_signups}
                  </p>
                </div>
                <TrendingUp className="h-10 w-10 text-rose-500 opacity-50" />
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Compliance Stats */}
      {platformStats && (
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Platform Compliance Activity</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-3 gap-4">
              <div className="text-center p-4 bg-slate-50 dark:bg-slate-800 rounded-lg">
                <p className="text-3xl font-bold text-slate-900 dark:text-white">
                  {platformStats.total_ropa_records}
                </p>
                <p className="text-sm text-slate-600 dark:text-slate-400">ROPA Records</p>
              </div>
              <div className="text-center p-4 bg-slate-50 dark:bg-slate-800 rounded-lg">
                <p className="text-3xl font-bold text-slate-900 dark:text-white">
                  {platformStats.total_breaches}
                </p>
                <p className="text-sm text-slate-600 dark:text-slate-400">Breach Reports</p>
              </div>
              <div className="text-center p-4 bg-slate-50 dark:bg-slate-800 rounded-lg">
                <p className="text-3xl font-bold text-slate-900 dark:text-white">
                  {platformStats.total_dsrs}
                </p>
                <p className="text-sm text-slate-600 dark:text-slate-400">DSR Requests</p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Organizations Table */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="text-lg">Tenant Organizations</CardTitle>
            <div className="flex gap-2">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                <Input
                  placeholder="Search organizations..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-9 w-64"
                  data-testid="search-orgs"
                />
              </div>
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="w-32">
                  <SelectValue placeholder="Status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Status</SelectItem>
                  <SelectItem value="active">Active</SelectItem>
                  <SelectItem value="suspended">Suspended</SelectItem>
                  <SelectItem value="canceled">Canceled</SelectItem>
                  <SelectItem value="onboarding">Onboarding</SelectItem>
                </SelectContent>
              </Select>
              <Select value={tierFilter} onValueChange={setTierFilter}>
                <SelectTrigger className="w-32">
                  <SelectValue placeholder="Tier" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Tiers</SelectItem>
                  <SelectItem value="free">Free</SelectItem>
                  <SelectItem value="starter">Starter</SelectItem>
                  <SelectItem value="professional">Professional</SelectItem>
                  <SelectItem value="enterprise">Enterprise</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-slate-200 dark:border-slate-700">
                  <th className="text-left p-3 text-sm font-medium text-slate-600 dark:text-slate-400">Organization</th>
                  <th className="text-left p-3 text-sm font-medium text-slate-600 dark:text-slate-400">Status</th>
                  <th className="text-left p-3 text-sm font-medium text-slate-600 dark:text-slate-400">Tier</th>
                  <th className="text-center p-3 text-sm font-medium text-slate-600 dark:text-slate-400">Clients</th>
                  <th className="text-center p-3 text-sm font-medium text-slate-600 dark:text-slate-400">Users</th>
                  <th className="text-center p-3 text-sm font-medium text-slate-600 dark:text-slate-400">Entities</th>
                  <th className="text-left p-3 text-sm font-medium text-slate-600 dark:text-slate-400">Owner</th>
                  <th className="text-left p-3 text-sm font-medium text-slate-600 dark:text-slate-400">Created</th>
                  <th className="text-right p-3 text-sm font-medium text-slate-600 dark:text-slate-400">Actions</th>
                </tr>
              </thead>
              <tbody>
                {organizations.map((org) => (
                  <tr
                    key={org.id}
                    className="border-b border-slate-100 dark:border-slate-800 hover:bg-slate-50 dark:hover:bg-slate-800/50 cursor-pointer"
                    onClick={() => fetchOrgDetail(org.id)}
                    data-testid={`org-row-${org.id}`}
                  >
                    <td className="p-3">
                      <div>
                        <p className="font-medium text-slate-900 dark:text-white">{org.name}</p>
                        <p className="text-xs text-slate-500">{org.slug}</p>
                      </div>
                    </td>
                    <td className="p-3">{getStatusBadge(org.status)}</td>
                    <td className="p-3">{getTierBadge(org.subscription_tier)}</td>
                    <td className="p-3 text-center font-medium">{org.client_count}</td>
                    <td className="p-3 text-center font-medium">{org.user_count}</td>
                    <td className="p-3 text-center font-medium">{org.entity_count}</td>
                    <td className="p-3 text-sm text-slate-600 dark:text-slate-400">
                      {org.owner_email || org.contact_email || '-'}
                    </td>
                    <td className="p-3 text-sm text-slate-600 dark:text-slate-400">
                      {new Date(org.created_at).toLocaleDateString()}
                    </td>
                    <td className="p-3 text-right">
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
                          <Button variant="ghost" size="sm">
                            <MoreVertical className="h-4 w-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onClick={(e) => { e.stopPropagation(); fetchOrgDetail(org.id); }}>
                            <Eye className="h-4 w-4 mr-2" /> View Details
                          </DropdownMenuItem>
                          <DropdownMenuSeparator />
                          {org.status === 'active' ? (
                            <DropdownMenuItem
                              onClick={(e) => { e.stopPropagation(); updateOrgStatus(org.id, 'suspend'); }}
                              className="text-amber-600"
                            >
                              <Power className="h-4 w-4 mr-2" /> Suspend
                            </DropdownMenuItem>
                          ) : (
                            <DropdownMenuItem
                              onClick={(e) => { e.stopPropagation(); updateOrgStatus(org.id, 'activate'); }}
                              className="text-emerald-600"
                            >
                              <Check className="h-4 w-4 mr-2" /> Activate
                            </DropdownMenuItem>
                          )}
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      {/* Create Organization Dialog */}
      <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Create New Organization</DialogTitle>
            <DialogDescription>
              Set up a new tenant organization with an owner account
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Organization Name *</Label>
                <Input
                  value={newOrgForm.name}
                  onChange={(e) => setNewOrgForm({ ...newOrgForm, name: e.target.value })}
                  placeholder="Acme DPO Services"
                  data-testid="new-org-name"
                />
              </div>
              <div className="space-y-2">
                <Label>Slug *</Label>
                <Input
                  value={newOrgForm.slug}
                  onChange={(e) => setNewOrgForm({ ...newOrgForm, slug: e.target.value.toLowerCase().replace(/\s+/g, '-') })}
                  placeholder="acme-dpo"
                  data-testid="new-org-slug"
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Contact Email *</Label>
                <Input
                  type="email"
                  value={newOrgForm.contact_email}
                  onChange={(e) => setNewOrgForm({ ...newOrgForm, contact_email: e.target.value })}
                  placeholder="contact@acme.com"
                />
              </div>
              <div className="space-y-2">
                <Label>Subscription Tier</Label>
                <Select
                  value={newOrgForm.subscription_tier}
                  onValueChange={(v) => setNewOrgForm({ ...newOrgForm, subscription_tier: v })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="free">Free</SelectItem>
                    <SelectItem value="starter">Starter ($49/mo)</SelectItem>
                    <SelectItem value="professional">Professional ($149/mo)</SelectItem>
                    <SelectItem value="enterprise">Enterprise ($499/mo)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="border-t pt-4 mt-4">
              <h4 className="text-sm font-medium mb-3">Owner Account</h4>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Owner Name *</Label>
                  <Input
                    value={newOrgForm.owner_name}
                    onChange={(e) => setNewOrgForm({ ...newOrgForm, owner_name: e.target.value })}
                    placeholder="John Smith"
                    data-testid="new-org-owner-name"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Owner Email *</Label>
                  <Input
                    type="email"
                    value={newOrgForm.owner_email}
                    onChange={(e) => setNewOrgForm({ ...newOrgForm, owner_email: e.target.value })}
                    placeholder="john@acme.com"
                    data-testid="new-org-owner-email"
                  />
                </div>
              </div>
              <div className="space-y-2 mt-4">
                <Label>Temporary Password *</Label>
                <Input
                  type="password"
                  value={newOrgForm.owner_password}
                  onChange={(e) => setNewOrgForm({ ...newOrgForm, owner_password: e.target.value })}
                  placeholder="••••••••"
                  data-testid="new-org-owner-password"
                />
                <p className="text-xs text-slate-500">Owner will receive login credentials via email</p>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreateDialog(false)}>Cancel</Button>
            <Button onClick={createOrganization} data-testid="submit-new-org">
              Create Organization
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Organization Detail Dialog */}
      <Dialog open={showDetailDialog} onOpenChange={setShowDetailDialog}>
        <DialogContent className="max-w-2xl">
          {selectedOrg && (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  {selectedOrg.name}
                  {getStatusBadge(selectedOrg.status)}
                  {getTierBadge(selectedOrg.subscription_tier)}
                </DialogTitle>
                <DialogDescription>
                  {selectedOrg.slug} • Created {new Date(selectedOrg.created_at).toLocaleDateString()}
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-4 py-4">
                {/* Usage Stats */}
                <div className="grid grid-cols-4 gap-3">
                  <div className="text-center p-3 bg-slate-50 dark:bg-slate-800 rounded-lg">
                    <p className="text-2xl font-bold">{selectedOrg.client_count}</p>
                    <p className="text-xs text-slate-500">Clients</p>
                  </div>
                  <div className="text-center p-3 bg-slate-50 dark:bg-slate-800 rounded-lg">
                    <p className="text-2xl font-bold">{selectedOrg.user_count}</p>
                    <p className="text-xs text-slate-500">Users</p>
                  </div>
                  <div className="text-center p-3 bg-slate-50 dark:bg-slate-800 rounded-lg">
                    <p className="text-2xl font-bold">{selectedOrg.entity_count}</p>
                    <p className="text-xs text-slate-500">Entities</p>
                  </div>
                  <div className="text-center p-3 bg-slate-50 dark:bg-slate-800 rounded-lg">
                    <p className="text-2xl font-bold">{selectedOrg.ropa_count}</p>
                    <p className="text-xs text-slate-500">ROPA</p>
                  </div>
                </div>

                {/* Activity Stats */}
                <div className="grid grid-cols-3 gap-3">
                  <div className="p-3 bg-red-50 dark:bg-red-900/20 rounded-lg">
                    <div className="flex items-center gap-2">
                      <AlertTriangle className="h-4 w-4 text-red-500" />
                      <span className="text-sm font-medium">Breaches</span>
                    </div>
                    <p className="text-xl font-bold mt-1">{selectedOrg.breach_count}</p>
                  </div>
                  <div className="p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
                    <div className="flex items-center gap-2">
                      <Users className="h-4 w-4 text-blue-500" />
                      <span className="text-sm font-medium">DSRs</span>
                    </div>
                    <p className="text-xl font-bold mt-1">{selectedOrg.dsr_count}</p>
                  </div>
                  <div className="p-3 bg-purple-50 dark:bg-purple-900/20 rounded-lg">
                    <div className="flex items-center gap-2">
                      <Shield className="h-4 w-4 text-purple-500" />
                      <span className="text-sm font-medium">Vendors</span>
                    </div>
                    <p className="text-xl font-bold mt-1">{selectedOrg.vendor_count}</p>
                  </div>
                </div>

                {/* Contact & Limits */}
                <div className="grid grid-cols-2 gap-4 pt-4 border-t">
                  <div>
                    <h4 className="text-sm font-medium mb-2">Contact</h4>
                    <p className="text-sm text-slate-600 dark:text-slate-400">
                      {selectedOrg.contact_email || 'No contact email'}
                    </p>
                    <p className="text-sm text-slate-600 dark:text-slate-400 mt-1">
                      Owner: {selectedOrg.owner_email || 'Not assigned'}
                    </p>
                  </div>
                  <div>
                    <h4 className="text-sm font-medium mb-2">Limits</h4>
                    <div className="text-sm text-slate-600 dark:text-slate-400 space-y-1">
                      <p>Max Clients: {selectedOrg.limits?.max_clients || 'N/A'}</p>
                      <p>Max Users: {selectedOrg.limits?.max_users || 'N/A'}</p>
                      <p>Storage: {selectedOrg.limits?.storage_gb || 'N/A'} GB</p>
                    </div>
                  </div>
                </div>

                {/* Features */}
                <div className="pt-4 border-t">
                  <h4 className="text-sm font-medium mb-2">Enabled Features</h4>
                  <div className="flex flex-wrap gap-2">
                    {selectedOrg.features_enabled?.map((feature) => (
                      <Badge key={feature} variant="outline">{feature}</Badge>
                    ))}
                  </div>
                </div>
              </div>
              <DialogFooter>
                {selectedOrg.status === 'active' ? (
                  <Button
                    variant="outline"
                    className="text-amber-600"
                    onClick={() => updateOrgStatus(selectedOrg.id, 'suspend')}
                  >
                    <Power className="h-4 w-4 mr-2" /> Suspend Organization
                  </Button>
                ) : (
                  <Button
                    variant="outline"
                    className="text-emerald-600"
                    onClick={() => updateOrgStatus(selectedOrg.id, 'activate')}
                  >
                    <Check className="h-4 w-4 mr-2" /> Activate Organization
                  </Button>
                )}
                <Button variant="ghost" onClick={() => setShowDetailDialog(false)}>Close</Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default SuperAdminDashboard;
